import type { CoinifyProfitOptions } from "@/types/AlchemyTypes";
import { getDecomposeOutputProfits } from "./items";
import type { DecompOutput } from "@/types/DecompOutput";
import { applyMarketplaceTax } from "@/utils/marketplaceTax";

export class Item {
  constructor(
    public item: string,
    public name: string,
    public sellPrice: number,
    public isTradable: number,
    public itemLevel: number,
    public bulkMultiplier: number,
    public isCoinifiable: number,
    public decomposeItems: string,
    public transmuteSuccessRate: number,
    public transmuteDropTable: string,
    public buy: number,
    public sell: number,
    public decomp_profit?: number,
    public transmute_profit?: number,
    public decomp_revenue?: number,
    public decomp_cost?: number,
  ) {}

  setMarket(buy: number, sell: number) {
    this.buy = buy;
    this.sell = sell;
  }

  // transmute_profit is the expected value of the outputs, before the item's
  // own buy price is subtracted. Selling those outputs is what gets taxed.
  getTransmuteOutputValueAfterTax(taxPercent: number): number {
    if (this.transmute_profit === undefined) return Number.MIN_SAFE_INTEGER;

    return applyMarketplaceTax(this.transmute_profit, taxPercent);
  }

  // Without revenue and cost (items without market data or decompose outputs)
  // the untaxed profit is returned unchanged, including its sentinel value.
  getDecomposeProfitAfterTax(taxPercent: number): number {
    if (this.decomp_revenue === undefined || this.decomp_cost === undefined) {
      return this.decomp_profit ?? Number.MIN_SAFE_INTEGER;
    }

    return applyMarketplaceTax(this.decomp_revenue, taxPercent) - this.decomp_cost;
  }

  get CoinfifyOutput() {
    return this.sellPrice * 5 * this.bulkMultiplier;
  }

  public readonly CoinfifyDuration: number = 10;
  public readonly CoinfifyBaseRate: number = 70;

  getCoinifySuccessRate(
    alchLevel: number = 100,
    useCataTea: boolean = false,
    useCoc: boolean,
  ): number {
    let base = this.CoinfifyBaseRate;
    if (useCataTea) base += this.CoinfifyBaseRate * 0.05;
    if (useCoc) base += this.CoinfifyBaseRate * 0.15;

    return base;
  }

  getCoinifyProfit({
    useCatalystTea = false,
    catalystTeaPrice,
    useCoc = false,
    cocPrice,
  }: CoinifyProfitOptions): number {
    if (this.buy > 0) {
      let p =
        this.CoinfifyOutput *
          (this.getCoinifySuccessRate(100, useCatalystTea, useCoc) / 100) -
        this.buy * this.bulkMultiplier;
      if (useCatalystTea && catalystTeaPrice) {
        p -= catalystTeaPrice / (300 / 10);
      }

      if (useCoc && cocPrice) {
        p -= cocPrice;
      }

      return p;
    } else {
      return Number.NEGATIVE_INFINITY;
    }
  }

  getCoinifyProfitBuyThreshold({
    useCatalystTea = false,
    catalystTeaPrice,
    useCoc = false,
    cocPrice,
  }: CoinifyProfitOptions): number {
    let p =
      (this.CoinfifyOutput *
        (this.getCoinifySuccessRate(100, useCatalystTea, useCoc) / 100)) /
      this.bulkMultiplier;
    if (useCatalystTea && catalystTeaPrice) {
      p -= catalystTeaPrice / (300 / 10);
    }

    if (useCoc && cocPrice) {
      p -= cocPrice;
    }

    return p;
  }
}

export type ItemDto = {
  item: string;
  name: string;
  sellPrice: number;
  isTradable: number;
  itemLevel: number;
  bulkMultiplier: number;
  isCoinifiable: number;
  decomposeItems: string;
  transmuteSuccessRate: number;
  transmuteDropTable: string;
  buy: number;
  sell: number;
  decomp_profit?: number;
  transmute_profit?: number;
  decomp_revenue?: number;
  decomp_cost?: number;
};

export function rehydrateItem(dto: ItemDto): Item {
  return new Item(
    dto.item,
    dto.name,
    dto.sellPrice,
    dto.isTradable,
    dto.itemLevel,
    dto.bulkMultiplier,
    dto.isCoinifiable,
    dto.decomposeItems,
    dto.transmuteSuccessRate,
    dto.transmuteDropTable,
    dto.buy,
    dto.sell,
    dto.decomp_profit ?? Number.MIN_SAFE_INTEGER,
    dto.transmute_profit ?? Number.MIN_SAFE_INTEGER,
    dto.decomp_revenue ?? undefined,
    dto.decomp_cost ?? undefined,
  );
}

export function rehydrateItems(dtos: ItemDto[]): Item[] {
  return dtos.map(rehydrateItem);
}
